"use client"

import { Group } from "@visx/group"
import { ParentSize } from "@visx/responsive"
import { scaleLinear, scaleTime } from "@visx/scale"
import { LinePath } from "@visx/shape"
import type { MouseEvent } from "react"
import { useMemo, useState } from "react"
import { classNames } from "../../../utils"
import { ChartTooltip } from "../ChartTooltip"
import type { RenderTooltipFn } from "../types"

export type SparkLineChartProps<T> = {
  /**
   * Data points to be displayed on the chart.
   * Each point should include a value and a date.
   */
  data: T[]
  getValue: (d: T) => number
  getDate: (d: T) => Date
  renderTooltip?: RenderTooltipFn<T>
  className?: string
}

type InternalDatum<T> = {
  original: T
  x: Date
  y: number
}

function isDate(value: Date | number): value is Date {
  return value instanceof Date
}

export function SparkLineChart<T>({
  data,
  getDate,
  getValue,
  className,
  renderTooltip: RenderTooltip,
  ...rest
}: SparkLineChartProps<T>) {
  // Helpers to normalize x values
  const toMs = (x: Date | number): number =>
    isDate(x) ? x.getTime() : Number(x)
  const toDate = (x: Date | number): Date =>
    isDate(x) ? (x as Date) : new Date(Number(x))

  // Raw points derived from input
  const points: InternalDatum<T>[] = useMemo(
    () =>
      data?.map(d => ({
        original: d,
        x: getDate(d),
        y: getValue(d),
      })) ?? [],
    [data, getDate, getValue],
  )

  const firstValue = points[0]?.y
  const lastValue = points.at(-1)?.y

  // Significant point-to-point change threshold (0.1%)
  const hasVolatility = points.some((point, i) => {
    if (i === 0) {
      return false
    }
    const prev = points[i - 1]
    if (prev.y === 0) {
      return false
    }
    const pct = Math.abs(((point.y - prev.y) / prev.y) * 100)
    return pct >= 0.1
  })

  const isFlat = !hasVolatility || firstValue === lastValue

  const change: "positive" | "negative" | "none" = isFlat
    ? "none"
    : lastValue != null && firstValue != null && lastValue > firstValue
      ? "positive"
      : "negative"

  const displayPoints: InternalDatum<T>[] = useMemo(
    () => (isFlat ? points.map(p => ({ ...p, y: firstValue })) : points),
    [isFlat, points, firstValue],
  )

  const xValues = useMemo(
    () => displayPoints.map(d => toMs(d.x)),
    [displayPoints],
  )
  const yValues = useMemo(() => displayPoints.map(d => d.y), [displayPoints])

  // Tooltip state and handlers
  const [hover, setHover] = useState<{
    index: number
    x: number
    y: number
  } | null>(null)

  if (!data || data.length <= 1) {
    return null
  }

  const margin = { top: 3, right: 3, bottom: 3, left: 3 }

  const strokeClass =
    change === "positive"
      ? "stroke-success-1"
      : change === "negative"
        ? "stroke-error-1"
        : "stroke-border-2"

  // Tooltip anchor managed by ChartTooltip

  const handlePointerMoveFactory =
    (
      xScale: ReturnType<typeof scaleTime<number>>,
      yScale: ReturnType<typeof scaleLinear<number>>,
    ) =>
    (clientX: number, currentTarget: SVGRectElement) => {
      if (!RenderTooltip) {
        return
      }
      const rect = currentTarget.getBoundingClientRect()
      const px = clientX - rect.left
      const xDate = xScale.invert(px)
      const xTime = xDate.getTime()

      let nearestIndex = 0
      let nearestDist = Number.POSITIVE_INFINITY
      for (let i = 0; i < points.length; i += 1) {
        const t = toMs(points[i].x)
        const d = Math.abs(t - xTime)
        if (d < nearestDist) {
          nearestDist = d
          nearestIndex = i
        }
      }

      const nearest = points[nearestIndex]
      const sx = xScale(toDate(nearest.x))
      const sy = yScale(isFlat ? firstValue : nearest.y)
      setHover({ index: nearestIndex, x: sx + margin.left, y: sy + margin.top })
    }

  const onMouseMoveFactory =
    (
      xScale: ReturnType<typeof scaleTime<number>>,
      yScale: ReturnType<typeof scaleLinear<number>>,
    ) =>
    (evt: MouseEvent<SVGRectElement>) => {
      handlePointerMoveFactory(xScale, yScale)(
        evt.clientX,
        evt.currentTarget as SVGRectElement,
      )
    }

  const onTouchStart =
    (
      xScale: ReturnType<typeof scaleTime<number>>,
      yScale: ReturnType<typeof scaleLinear<number>>,
    ) =>
    (evt: React.TouchEvent<SVGRectElement>) => {
      if (evt.touches.length > 0) {
        const touch = evt.touches[0]
        handlePointerMoveFactory(xScale, yScale)(
          touch.clientX,
          evt.currentTarget as SVGRectElement,
        )
      }
    }

  const onTouchMove =
    (
      xScale: ReturnType<typeof scaleTime<number>>,
      yScale: ReturnType<typeof scaleLinear<number>>,
    ) =>
    (evt: React.TouchEvent<SVGRectElement>) => {
      if (evt.touches.length > 0) {
        const touch = evt.touches[0]
        handlePointerMoveFactory(xScale, yScale)(
          touch.clientX,
          evt.currentTarget as SVGRectElement,
        )
      }
    }

  const onMouseLeave = () => setHover(null)
  const onTouchEnd = () => setHover(null)

  return (
    <div className={classNames("relative", className)} {...rest}>
      <ParentSize>
        {({ width, height }) => {
          const innerWidth = Math.max(0, width - margin.left - margin.right)
          const innerHeight = Math.max(0, height - margin.top - margin.bottom)

          const xMin = Math.min(...xValues)
          const xMax = Math.max(...xValues)
          let yMin = Math.min(...yValues)
          let yMax = Math.max(...yValues)

          if (yMin === yMax) {
            const pad = Math.max(1, Math.abs(yMax || 1) * 0.01)
            yMin -= pad
            yMax += pad
          } else {
            const mid = (yMin + yMax) / 2
            const halfSpan = (yMax - yMin) / 2
            const minHalfSpan = Math.max(halfSpan, Math.abs(mid) * 0.01)
            yMin = mid - minHalfSpan
            yMax = mid + minHalfSpan
          }

          const xScale = scaleTime<number>({
            domain: [new Date(xMin), new Date(xMax)],
            range: [0, innerWidth],
          })

          const yScale = scaleLinear<number>({
            domain: [yMin, yMax],
            range: [innerHeight, 0],
          })

          return (
            <svg
              aria-label="sparkline"
              height={height}
              role="img"
              width={width}
            >
              <Group left={margin.left} top={margin.top}>
                <LinePath
                  className={classNames(strokeClass)}
                  data={displayPoints}
                  strokeOpacity={1}
                  strokeWidth={2}
                  x={d => xScale(d.x)}
                  y={d => yScale(d.y)}
                />
                {RenderTooltip && hover ? (
                  <circle
                    className="fill-blue-3 stroke-blue-3"
                    cx={hover.x - margin.left}
                    cy={hover.y - margin.top}
                    pointerEvents="none"
                    r={2}
                  />
                ) : null}
                {/* Interaction overlay for tooltip */}
                <rect
                  fill="transparent"
                  height={innerHeight}
                  onMouseLeave={onMouseLeave}
                  onMouseMove={onMouseMoveFactory(xScale, yScale)}
                  onTouchEnd={onTouchEnd}
                  onTouchMove={onTouchMove(xScale, yScale)}
                  onTouchStart={onTouchStart(xScale, yScale)}
                  width={innerWidth}
                />
              </Group>
            </svg>
          )
        }}
      </ParentSize>
      {RenderTooltip ? (
        <ChartTooltip
          animation="none"
          content={
            hover ? <RenderTooltip item={points[hover.index].original} /> : null
          }
          hover={hover ? { x: hover.x, y: hover.y } : null}
        />
      ) : null}
    </div>
  )
}
